import { FC, useState } from "react";
import { Handle, Position } from "reactflow";
import { Play, Calendar, Globe, Zap, Hand } from "lucide-react";
import { Badge, Card, Flex, Text } from "@radix-ui/themes";
import {
  WorkflowTrigger,
  WorkflowTriggerType,
} from "@/types/breeze/Workflow";
import { getTriggerLabel } from "@/utils/workflow";

interface StartNodeProps {
  data: {
    onTest?: () => void;
    triggers?: WorkflowTrigger[];
  };
}

const TRIGGER_ICONS: Record<WorkflowTriggerType, typeof Play> = {
  [WorkflowTriggerType.None]: Play,
  [WorkflowTriggerType.Api]: Globe,
  [WorkflowTriggerType.Event]: Zap,
  [WorkflowTriggerType.Timed]: Calendar,
  [WorkflowTriggerType.Manual]: Hand,
};

export const StartNode: FC<StartNodeProps> = ({ data }) => {
  const [hovered, setHovered] = useState(false);
  const canTest = !!data.onTest;
  const triggers = data.triggers ?? [];

  return (
    <div style={{ position: "relative" }}>
      <Handle
        type="source"
        position={Position.Right}
        style={{
          background: "var(--accent-9)",
          width: "12px",
          height: "12px",
          zIndex: 1000,
        }}
        id="start"
      />

      <Card
        variant="classic"
        style={{
          minWidth: 160,
          background: hovered && canTest ? "var(--accent-3)" : "var(--accent-2)",
          border: "2px solid var(--accent-6)",
          cursor: canTest ? "pointer" : "default",
          transition: "all 0.15s ease",
          boxShadow: hovered && canTest ? "0 0 0 2px var(--accent-9)" : "none",
        }}
        onClick={canTest ? data.onTest : undefined}
        onMouseEnter={() => setHovered(true)}
        onMouseLeave={() => setHovered(false)}
      >
        <Flex direction="column" gap="2" p="3" align="center">
          <Play
            size={20}
            style={{
              color: "var(--accent-11)",
              fill: "var(--accent-9)",
            }}
          />
          <Text
            size="2"
            weight="bold"
            color="blue"
            highContrast
            style={{ textAlign: "center" }}
          >
            {hovered && canTest ? "Test" : "Start"}
          </Text>

          {triggers.length > 0 && (
            <Flex direction="column" gap="1" align="stretch" width="100%">
              {triggers.map((trigger, index) => {
                const Icon = TRIGGER_ICONS[trigger.type] ?? Play;
                return (
                  <Badge
                    key={trigger.id ?? index}
                    variant="soft"
                    color={trigger.enabled ? "blue" : "gray"}
                    style={{ justifyContent: "center" }}
                  >
                    <Icon size={12} />
                    <Text size="1">{getTriggerLabel(trigger.type)}</Text>
                  </Badge>
                );
              })}
            </Flex>
          )}
        </Flex>
      </Card>
    </div>
  );
};
